import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Camera, ArrowLeft, Briefcase, Lightbulb, Network, CheckSquare, Square } from 'lucide-react';

// Get backend URL for API calls
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Application() {
  const { signUp, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const [accountData, setAccountData] = useState({ email: '', password: '', confirmPassword: '' });
  const [formData, setFormData] = useState({
    fullName: '', phoneNumber: '', profession: '', company: '', role: '', industry: '', yearsOfExperience: '', educationCredentials: '',
    areasOfInterest: [], whyJoin: '', howHeard: '', contribution: '', notableAchievements: '', networkingGoals: '', linkedinUrl: '', instagramUrl: '', desiredTier: 'founder'
  });
  const [headshotFile, setHeadshotFile] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeadshotFile(file);
      setHeadshotPreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (file, userId) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `headshots/${fileName}`;
    const { error: uploadError } = await supabase.storage.from('member-photos').upload(filePath, file);
    if (uploadError) throw uploadError;
    const { data: { publicUrl } } = supabase.storage.from('member-photos').getPublicUrl(filePath);
    return publicUrl;
  };

  const handleStep1Continue = () => {
    setError('');
    if (!accountData.email || !accountData.password || !accountData.confirmPassword) { setError('Please fill in all fields'); return; }
    if (accountData.password !== accountData.confirmPassword) { setError('Passwords do not match'); return; }
    if (accountData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setStep(2);
  };

  const handleStep2Continue = () => {
    setError('');
    if (!formData.fullName || !headshotFile || !formData.linkedinUrl || !formData.instagramUrl) { setError('Please fill in all required fields including social media profiles'); return; }
    setStep(3);
  };

  const handleStep3Continue = () => {
    setError('');
    if (!formData.profession || !formData.company || !formData.industry || !formData.yearsOfExperience) { setError('Please fill in all required fields'); return; }
    setStep(4);
  };

  const handleStep4Continue = () => {
    setError('');
    if (formData.areasOfInterest.length === 0 || !formData.whyJoin || !formData.contribution || !formData.howHeard) { setError('Please fill in all required fields'); return; }
    setStep(5);
  };

  const handleStep5Continue = () => {
    setError('');
    if (!formData.networkingGoals) { setError('Please describe your networking goals'); return; }
    if (!agreedToTerms) { setError('Please agree to the Terms & Conditions to continue'); return; }
    if (!agreedToPrivacy) { setError('Please acknowledge the Privacy Policy to continue'); return; }
    handleSubmit();
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      areasOfInterest: prev.areasOfInterest.includes(interest)
        ? prev.areasOfInterest.filter(i => i !== interest)
        : [...prev.areasOfInterest, interest]
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      let userId;
      const { data: authData, error: authError } = await signUp(accountData.email, accountData.password);
      if (authError) {
        if (authError.message?.includes('already registered') || authError.message?.includes('user_already_exists')) {
          const { data: { user: existingUser } } = await supabase.auth.signInWithPassword({ email: accountData.email, password: accountData.password });
          if (existingUser) userId = existingUser.id;
          else throw new Error('Account already exists but sign in failed.');
        } else throw authError;
      } else {
        userId = authData?.user?.id;
      }
      if (!userId) throw new Error('Failed to create account');

      const headshotUrl = await uploadImage(headshotFile, userId);
      const isAdminEmail = accountData.email.toLowerCase() === 'mau@mausanchez.com';

      await supabase.from('applications').insert({
        user_id: userId, email: accountData.email, full_name: formData.fullName, phone_number: formData.phoneNumber, profession: formData.profession, company: formData.company, role: formData.role,
        industry: formData.industry, years_of_experience: formData.yearsOfExperience, education_credentials: formData.educationCredentials, areas_of_interest: formData.areasOfInterest,
        why_join: formData.whyJoin, how_heard: formData.howHeard, contribution: formData.contribution, notable_achievements: formData.notableAchievements || null, networking_goals: formData.networkingGoals,
        background: null, linkedin_url: formData.linkedinUrl || null, instagram_url: formData.instagramUrl || null, headshot_url: headshotUrl, desired_tier: formData.desiredTier,
        status: isAdminEmail ? 'approved' : 'pending', reviewed_at: isAdminEmail ? new Date().toISOString() : null,
        terms_accepted: agreedToTerms, privacy_accepted: agreedToPrivacy, terms_accepted_at: new Date().toISOString(),
      });

      if (isAdminEmail) {
        await supabase.from('profiles').insert({
          user_id: userId, full_name: formData.fullName, phone_number: formData.phoneNumber, headshot_url: headshotUrl, company: formData.company, role: formData.role,
          work_description: formData.profession, linkedin_url: formData.linkedinUrl || null, instagram_url: formData.instagramUrl || null, membership_tier: formData.desiredTier,
          membership_status: 'active', member_number: 1, initiation_fee_waived: true, payment_status: 'paid',
        });
      }

      // Send email notifications for non-admin applications
      if (!isAdminEmail) {
        try {
          // Get all admin emails - check profiles table for admin email field first
          const { data: adminProfiles } = await supabase
            .from('profiles')
            .select('user_id, email')
            .eq('admin_role', true);
          
          let adminEmails = [];
          
          if (adminProfiles && adminProfiles.length > 0) {
            // First try to get emails directly from profiles if available
            adminEmails = adminProfiles.map(p => p.email).filter(Boolean);
            
            // If no emails in profiles, try to get from applications table
            if (adminEmails.length === 0) {
              const adminUserIds = adminProfiles.map(p => p.user_id);
              const { data: adminApps } = await supabase
                .from('applications')
                .select('email')
                .in('user_id', adminUserIds);
              
              if (adminApps) {
                adminEmails = adminApps.map(a => a.email).filter(Boolean);
              }
            }
          }
          
          // Fallback to primary admin email if no admins found
          if (adminEmails.length === 0) {
            adminEmails = ['mau@mausanchez.com'];
          }

          // Send application submitted notification
          await fetch(`${API_URL}/api/email/application-submitted`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              applicant_email: accountData.email,
              applicant_name: formData.fullName,
              admin_emails: adminEmails
            })
          });
        } catch (emailError) {
          console.error('Failed to send notification emails:', emailError);
          // Don't fail the application if email fails
        }
      }

      await refreshUserData();
      setLoading(false);
      navigate('/pending');
    } catch (err) {
      setError(err.message || 'Failed to submit application');
      setLoading(false);
    }
  };

  const interests = ['ECONOMICS & FINANCE', 'INVESTMENT STRATEGY', 'BUSINESS DEVELOPMENT', 'ENTREPRENEURSHIP', 'GLOBAL MARKETS', 'REAL ESTATE', 'TECHNOLOGY INNOVATION', 'PUBLIC POLICY', 'PHILANTHROPY', 'LEADERSHIP DEVELOPMENT'];

  // Step 1: Account
  if (step === 1) return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => navigate('/auth')} className="flex items-center text-brand-gold mb-6"><ArrowLeft size={20} className="mr-2" /> Back</button>
      <div className="text-center mb-8"><h1 className="text-2xl font-bold text-brand-gold">Economic Club of SA</h1></div>
      <h2 className="text-2xl font-bold text-brand-gold text-center mb-2">Create Your Account</h2>
      <p className="text-gray-400 text-center mb-8">Step 1 of 5: Set up your account credentials</p>
      {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      <div className="bg-white/5 border border-brand-gold-border rounded-2xl p-6 space-y-4">
        <div><label className="text-sm font-semibold text-white mb-2 block">Email *</label><input type="email" placeholder="your.email@example.com" value={accountData.email} onChange={(e) => setAccountData({ ...accountData, email: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" data-testid="email-input" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">Password *</label><input type="password" placeholder="Minimum 6 characters" value={accountData.password} onChange={(e) => setAccountData({ ...accountData, password: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" data-testid="password-input" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">Confirm Password *</label><input type="password" placeholder="Re-enter your password" value={accountData.confirmPassword} onChange={(e) => setAccountData({ ...accountData, confirmPassword: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" data-testid="confirm-password-input" /></div>
      </div>
      <button onClick={handleStep1Continue} className="w-full mt-6 py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors" data-testid="continue-btn">Continue to Application</button>
    </div>
  );

  // Step 2: Personal Info
  if (step === 2) return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => setStep(1)} className="flex items-center text-brand-gold mb-6"><ArrowLeft size={20} className="mr-2" /> Previous</button>
      <div className="w-24 h-24 bg-brand-gold-light rounded-full flex items-center justify-center mx-auto mb-6"><Camera size={48} className="text-brand-gold" /></div>
      <h2 className="text-2xl font-bold text-brand-gold text-center mb-2">Personal Information</h2>
      <p className="text-gray-400 text-center mb-8">Tell us about yourself</p>
      {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      <div className="space-y-4">
        <div><label className="text-sm font-semibold text-white mb-2 block">EMAIL *</label><input type="email" value={accountData.email} disabled className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-gray-400 opacity-60" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">FULL NAME *</label><input type="text" placeholder="John Doe" value={formData.fullName} onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" data-testid="fullname-input" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">PHONE NUMBER</label><input type="tel" placeholder="(210) 555-0123" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">LINKEDIN PROFILE URL *</label><input type="url" placeholder="https://linkedin.com/in/yourprofile" value={formData.linkedinUrl} onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">INSTAGRAM PROFILE URL *</label><input type="url" placeholder="https://instagram.com/yourprofile" value={formData.instagramUrl} onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">PROFESSIONAL HEADSHOT *</label>
          <label className="block cursor-pointer">
            <div className="h-56 border-2 border-dashed border-brand-gold-border rounded-xl flex flex-col items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
              {headshotPreview ? <img src={headshotPreview} alt="Preview" className="w-full h-full object-cover rounded-xl" /> : <><Camera size={48} className="text-brand-gold mb-2" /><span className="text-brand-gold font-semibold">Upload Photo</span></>}
            </div>
            <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="headshot-input" />
          </label>
        </div>
      </div>
      <button onClick={handleStep2Continue} className="w-full mt-6 py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors" data-testid="continue-btn">Continue</button>
    </div>
  );

  // Step 3: Professional Background
  if (step === 3) return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => setStep(2)} className="flex items-center text-brand-gold mb-6"><ArrowLeft size={20} className="mr-2" /> Previous</button>
      <div className="w-24 h-24 bg-brand-gold-light rounded-full flex items-center justify-center mx-auto mb-6"><Briefcase size={48} className="text-brand-gold" /></div>
      <h2 className="text-2xl font-bold text-brand-gold text-center mb-2">Professional Background</h2>
      <p className="text-gray-400 text-center mb-8">Tell us about your career and expertise</p>
      {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      <div className="space-y-4">
        <div><label className="text-sm font-semibold text-white mb-2 block">CURRENT OCCUPATION / TITLE *</label><input type="text" placeholder="e.g., Chief Financial Officer" value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" data-testid="profession-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-semibold text-white mb-2 block">COMPANY</label><input type="text" placeholder="Company name" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
          <div><label className="text-sm font-semibold text-white mb-2 block">INDUSTRY *</label><input type="text" placeholder="e.g., Finance" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
        </div>
        <div><label className="text-sm font-semibold text-white mb-2 block">YEARS OF EXPERIENCE *</label><input type="text" placeholder="e.g., 10-15 years" value={formData.yearsOfExperience} onChange={(e) => setFormData({ ...formData, yearsOfExperience: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">EDUCATION & CREDENTIALS</label><textarea placeholder="Degrees, certifications, institutions..." value={formData.educationCredentials} onChange={(e) => setFormData({ ...formData, educationCredentials: e.target.value })} rows={3} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold resize-none" /></div>
      </div>
      <button onClick={handleStep3Continue} className="w-full mt-6 py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors" data-testid="continue-btn">Continue</button>
    </div>
  );

  // Step 4: Interests & Motivations
  if (step === 4) return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => setStep(3)} className="flex items-center text-brand-gold mb-6"><ArrowLeft size={20} className="mr-2" /> Previous</button>
      <div className="w-24 h-24 bg-brand-gold-light rounded-full flex items-center justify-center mx-auto mb-6"><Lightbulb size={48} className="text-brand-gold" /></div>
      <h2 className="text-2xl font-bold text-brand-gold text-center mb-2">Interests & Motivations</h2>
      <p className="text-gray-400 text-center mb-8">Share your professional interests and goals</p>
      {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      <div className="space-y-4">
        <div><label className="text-sm font-semibold text-white mb-2 block">AREAS OF INTEREST * (SELECT ALL THAT APPLY)</label>
          <div className="flex flex-wrap gap-2">{interests.map((interest) => (<button key={interest} type="button" onClick={() => toggleInterest(interest)} className={`px-4 py-2 rounded-lg text-xs font-semibold border-2 transition-colors ${formData.areasOfInterest.includes(interest) ? 'bg-brand-gold-light border-brand-gold text-brand-gold' : 'bg-black/30 border-brand-gold-border text-gray-400 hover:border-brand-gold/50'}`}>{interest}</button>))}</div>
        </div>
        <div><label className="text-sm font-semibold text-white mb-2 block">WHY DO YOU WANT TO JOIN? *</label><textarea placeholder="Share your motivations..." value={formData.whyJoin} onChange={(e) => setFormData({ ...formData, whyJoin: e.target.value })} rows={4} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold resize-none" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">WHAT VALUE CAN YOU BRING? *</label><textarea placeholder="Describe your unique skills..." value={formData.contribution} onChange={(e) => setFormData({ ...formData, contribution: e.target.value })} rows={4} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold resize-none" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">NOTABLE ACHIEVEMENTS (OPTIONAL)</label><textarea placeholder="Awards, publications, board positions..." value={formData.notableAchievements} onChange={(e) => setFormData({ ...formData, notableAchievements: e.target.value })} rows={3} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold resize-none" /></div>
        <div><label className="text-sm font-semibold text-white mb-2 block">HOW DID YOU HEAR ABOUT US? *</label><input type="text" placeholder="e.g., Friend referral, social media..." value={formData.howHeard} onChange={(e) => setFormData({ ...formData, howHeard: e.target.value })} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold" /></div>
      </div>
      <button onClick={handleStep4Continue} className="w-full mt-6 py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors" data-testid="continue-btn">Continue</button>
    </div>
  );

  // Step 5: Networking Goals
  if (step === 5) return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => setStep(4)} className="flex items-center text-brand-gold mb-6"><ArrowLeft size={20} className="mr-2" /> Previous</button>
      <div className="w-24 h-24 bg-brand-gold-light rounded-full flex items-center justify-center mx-auto mb-6"><Network size={48} className="text-brand-gold" /></div>
      <h2 className="text-2xl font-bold text-brand-gold text-center mb-2">Networking Goals</h2>
      <p className="text-gray-400 text-center mb-8">Help us understand your objectives</p>
      {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>}
      <div><label className="text-sm font-semibold text-white mb-2 block">WHAT ARE YOUR PRIMARY NETWORKING GOALS? *</label><textarea placeholder="Describe the types of connections, opportunities, or knowledge you're seeking..." value={formData.networkingGoals} onChange={(e) => setFormData({ ...formData, networkingGoals: e.target.value })} rows={6} className="w-full px-4 py-4 bg-black/30 border border-brand-gold-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold resize-none" /></div>
      
      {/* Terms & Privacy Checkboxes */}
      <div className="mt-6 space-y-4">
        <div 
          className="flex items-start gap-3 p-4 rounded-xl bg-black/20 border border-brand-gold/20 cursor-pointer hover:border-brand-gold/40 transition-colors"
          onClick={() => setAgreedToTerms(!agreedToTerms)}
        >
          {agreedToTerms ? (
            <CheckSquare size={22} className="text-brand-gold flex-shrink-0 mt-0.5" />
          ) : (
            <Square size={22} className="text-gray-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            <span className="text-gray-300">I agree to the ECSA </span>
            <Link 
              to="/terms" 
              target="_blank" 
              className="text-brand-gold hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Terms & Conditions
            </Link>
            <span className="text-gray-300">.</span>
            <span className="text-red-400 ml-1">*</span>
          </div>
        </div>
        
        <div 
          className="flex items-start gap-3 p-4 rounded-xl bg-black/20 border border-brand-gold/20 cursor-pointer hover:border-brand-gold/40 transition-colors"
          onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
        >
          {agreedToPrivacy ? (
            <CheckSquare size={22} className="text-brand-gold flex-shrink-0 mt-0.5" />
          ) : (
            <Square size={22} className="text-gray-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            <span className="text-gray-300">I understand my submitted information (including member directory and event participation data) may be shared with ECSA sponsors and affiliates as described in the </span>
            <Link 
              to="/privacy" 
              target="_blank" 
              className="text-brand-gold hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </Link>
            <span className="text-gray-300">.</span>
            <span className="text-red-400 ml-1">*</span>
          </div>
        </div>
      </div>
      
      <button onClick={handleStep5Continue} disabled={loading} className="w-full mt-6 py-4 bg-brand-gold text-brand-dark font-bold rounded-xl hover:bg-yellow-400 transition-colors disabled:opacity-50" data-testid="submit-btn">{loading ? 'Submitting...' : 'Submit Application'}</button>
    </div>
  );

  return null;
}
